import { useEffect, useMemo, useRef, useState } from "react";

import type { StockRuntimeItem } from "../runtime/gateway/local-projection-client";
import { commitStockInternalUseBatch } from "../runtime/local-store/stock-action-state";
import { readLocalStockMovementHistory } from "../runtime/local-store/stock-movement-runtime";
import type { TrustedTerminalIdentity } from "../runtime/local-store/terminal-state";
import "./StockInternalUseBatch.css";

type DraftLine = {
  item: StockRuntimeItem;
  quantity: string;
  unitName: string;
};

type Props = {
  identity: TrustedTerminalIdentity;
  deviceSecret: string;
  sessionToken: string;
  items: StockRuntimeItem[];
  seedItem?: StockRuntimeItem | null;
  online: boolean;
  requiresNote: boolean;
  onClose: () => void;
};

function requestKey() {
  const id = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `pos-stock-internal-use-batch:${id}`;
}

function quantityLabel(value: number | null) {
  if (value == null) return "—";
  return new Intl.NumberFormat("th-TH", { maximumFractionDigits: 3 }).format(Number(value));
}

function normalizedSearch(value: string) {
  return value.trim().toLocaleLowerCase("th-TH");
}

function defaultQuantity(_item: StockRuntimeItem) {
  return "1";
}

function lineError(line: DraftLine) {
  if (!line.quantity.trim()) return "ใส่จำนวน";
  const quantity = Number(line.quantity);
  if (!Number.isFinite(quantity) || quantity <= 0) return "จำนวนต้องมากกว่า 0";
  return null;
}

function businessError(value: string) {
  if (value.includes("STOCK_ACTION_ONLINE_REQUIRED") || value.includes("GATEWAY_UNREACHABLE")) {
    return "ตอนนี้ยังบันทึกไม่ได้ เพราะ Owner ยังไม่ได้ Publish Offline Authority สำหรับการเบิกใช้";
  }
  if (value.includes("STOCK_ACTION_PERMISSION_REQUIRED")) return "คุณไม่มีสิทธิ์เบิกใช้สต็อกในสาขานี้";
  if (value.includes("STOCK_ACTION_NOTE_REQUIRED")) return "กรุณาระบุรายละเอียดก่อนบันทึก";
  if (value.includes("STOCK_INTERNAL_USE_DUPLICATE_ITEM")) return "มีสินค้าซ้ำในชุด กรุณาตรวจรายการอีกครั้ง";
  if (value.includes("INSUFFICIENT_STOCK_FOR_ITEM") || value.includes("NEGATIVE_STOCK_NOT_ALLOWED")) {
    return "Stock Core ยังไม่อนุญาตรายการนี้ตามนโยบายปัจจุบัน กรุณารีเฟรชและตรวจอีกครั้ง";
  }
  if (value.includes("INITIAL_STOCK_COUNT_REQUIRED")) return "มีสินค้าบางรายการที่ยังไม่มี Canonical Stock State สำหรับทำรายการ";
  if (value.includes("STOCK_TRACKING_POLICY_REQUIRED") || value.includes("STOCK_TRACKING_UNIT_REQUIRED")) {
    return "มีสินค้าบางรายการที่ยังตั้งค่าหน่วยสต็อกไม่สมบูรณ์";
  }
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID")) return "เซสชันพนักงานหมดอายุ กรุณาเข้าสู่ระบบพนักงานใหม่";
  if (value.includes("STOCK_ACTION_REQUEST_KEY_CONFLICT")) return "ชุดรายการเปลี่ยนหลังเริ่มบันทึก กรุณาตรวจสอบแล้วส่งใหม่";
  return "บันทึกชุดเบิกใช้ไม่สำเร็จ กรุณาตรวจรายการแล้วลองใหม่";
}

export function StockInternalUseBatch({
  identity,
  deviceSecret,
  sessionToken,
  items,
  seedItem = null,
  online,
  requiresNote,
  onClose,
}: Props) {
  const initialSeed = seedItem;
  const [draft, setDraft] = useState<DraftLine[]>(() => initialSeed ? [{ item: initialSeed, quantity: defaultQuantity(initialSeed), unitName: "หน่วยสต็อก" }] : []);
  const [query, setQuery] = useState("");
  const [note, setNote] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [duplicateItemId, setDuplicateItemId] = useState<string | null>(null);
  const [activeRequestKey, setActiveRequestKey] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const syncViewport = () => {
      const backdrop = backdropRef.current;
      if (!backdrop) return;
      backdrop.style.setProperty("--pos-stock-visible-height", `${Math.max(1, viewport.height)}px`);
      backdrop.style.setProperty("--pos-stock-visible-offset", `${Math.max(0, viewport.offsetTop)}px`);
    };

    syncViewport();
    viewport.addEventListener("resize", syncViewport);
    viewport.addEventListener("scroll", syncViewport);
    return () => {
      viewport.removeEventListener("resize", syncViewport);
      viewport.removeEventListener("scroll", syncViewport);
    };
  }, []);

  const addedIds = useMemo(() => new Set(draft.map((line) => line.item.stock_item_id)), [draft]);
  const searchText = normalizedSearch(query);
  const candidates = useMemo(() => items
    .filter((item) => item.active)
    .filter((item) => {
      if (!searchText) return true;
      const haystack = [
        item.canonical_name,
        item.item_code,
        item.sku_code ?? "",
        ...(item.barcodes ?? []),
      ].join(" ").toLocaleLowerCase("th-TH");
      return haystack.includes(searchText);
    })
    .sort((left, right) => left.canonical_name.localeCompare(right.canonical_name, "th"))
    .slice(0, 120), [items, searchText]);

  const errors = useMemo(() => new Map(draft.map((line) => [line.item.stock_item_id, lineError(line)])), [draft]);
  const invalidCount = [...errors.values()].filter(Boolean).length;
  const canReview = draft.length > 0 && invalidCount === 0 && (!requiresNote || Boolean(note.trim()));

  function draftChanged() {
    setActiveRequestKey("");
    setError(null);
    setReviewing(false);
  }

  function dismissSearchKeyboard() {
    searchInputRef.current?.blur();
    setSearchActive(false);
  }

  async function resolveUnitName(item: StockRuntimeItem) {
    try {
      const history = await readLocalStockMovementHistory(identity, item.stock_item_id);
      return history[0]?.unit_name || "หน่วยสต็อก";
    } catch {
      return "หน่วยสต็อก";
    }
  }

  async function addItem(item: StockRuntimeItem) {
    dismissSearchKeyboard();
    if (addedIds.has(item.stock_item_id)) {
      setDuplicateItemId(item.stock_item_id);
      setError("สินค้านี้อยู่ในชุดแล้ว แก้จำนวนที่รายการด้านขวาได้เลย");
      return;
    }
    const unitName = await resolveUnitName(item);
    setDraft((current) => [...current, { item, quantity: defaultQuantity(item), unitName }]);
    setDuplicateItemId(null);
    setQuery("");
    draftChanged();
  }

  function changeQuantity(stockItemId: string, value: string) {
    setDraft((current) => current.map((line) => line.item.stock_item_id === stockItemId ? { ...line, quantity: value } : line));
    setDuplicateItemId(null);
    draftChanged();
  }

  function stepQuantity(stockItemId: string, delta: -1 | 1) {
    const line = draft.find((candidate) => candidate.item.stock_item_id === stockItemId);
    if (!line) return;
    const quantity = Number(line.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) return;
    const next = quantity + delta;
    if (next <= 0) return;
    changeQuantity(stockItemId, String(next));
  }

  function removeItem(stockItemId: string) {
    setDraft((current) => current.filter((line) => line.item.stock_item_id !== stockItemId));
    setDuplicateItemId(null);
    draftChanged();
  }

  function changeNote(value: string) {
    setNote(value);
    draftChanged();
  }

  function startReview() {
    if (!draft.length) {
      setError("เพิ่มสินค้าอย่างน้อย 1 รายการก่อนตรวจสอบ");
      return;
    }
    if (!canReview) {
      setError(requiresNote && !note.trim() ? "กรุณาระบุรายละเอียดก่อนตรวจสอบ" : "ตรวจจำนวนที่ยังว่างหรือเกินยอดคงเหลือก่อน");
      return;
    }
    setError(null);
    setReviewing(true);
  }

  async function submit() {
    if (!canReview || busy || done) return;
    if (!online) {
      setError("ตอนนี้ยังบันทึกไม่ได้ขณะออฟไลน์ แต่รายการที่กำลังจัดยังแก้ไขได้ตามปกติ");
      return;
    }

    const key = activeRequestKey || requestKey();
    if (!activeRequestKey) setActiveRequestKey(key);
    setBusy(true);
    setError(null);
    try {
      const result = await commitStockInternalUseBatch({
        identity,
        deviceSecret,
        sessionToken,
        lines: draft.map((line) => ({ stockItemId: line.item.stock_item_id, quantity: Number(line.quantity) })),
        note: note.trim() || null,
        requestKey: key,
      });
      setSuccess(
        result.readbackOk
          ? `บันทึกเบิกใช้ ${draft.length.toLocaleString("th-TH")} รายการเรียบร้อยแล้ว`
          : `บันทึกแล้ว ${draft.length.toLocaleString("th-TH")} รายการ · กำลังซิงค์ยอดล่าสุดจาก Stock Core`,
      );
      setDone(true);
    } catch (cause) {
      setError(businessError(cause instanceof Error ? cause.message : "POS_STOCK_INTERNAL_USE_BATCH_COMMIT_FAILED"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div ref={backdropRef} className="pos-stock-batch-backdrop" role="presentation">
      <section
        className="pos-stock-batch"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pos-stock-batch-title"
        data-search-active={searchActive ? "true" : "false"}
      >
        <header className="pos-stock-batch__header">
          <div>
            <span>MORE Stock · {identity.branch_name}</span>
            <h2 id="pos-stock-batch-title">เบิกใช้ในร้าน</h2>
            <p>{reviewing ? "ตรวจทุกจำนวนก่อนบันทึกครั้งเดียว" : "เพิ่มหลายรายการให้จบในหน้าเดียว"}</p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="ปิด">×</button>
        </header>

        {done ? (
          <main className="pos-stock-batch__done">
            <div aria-hidden="true">✓</div>
            <h3>{success}</h3>
            <p>Stock Core รับเป็นหนึ่ง Batch และประวัติแต่ละสินค้าจะอัปเดตจาก Owner Read-back</p>
            <button type="button" onClick={onClose}>เสร็จสิ้น</button>
          </main>
        ) : (
          <>
            {!online ? <div className="pos-stock-batch__offline">ออฟไลน์ · จัดรายการและตรวจสอบได้ แต่ยังส่งบันทึกไม่ได้</div> : null}

            <main className="pos-stock-batch__body" data-reviewing={reviewing ? "true" : "false"}>
              {!reviewing ? (
                <section className="pos-stock-batch__picker" aria-label="เลือกสินค้า">
                  <div className="pos-stock-batch__search">
                    <div className="pos-stock-batch__search-heading">
                      <label htmlFor="pos-stock-batch-search">ค้นหา / สแกนสินค้า</label>
                      {searchActive ? (
                        <button
                          type="button"
                          onPointerDown={(event) => event.preventDefault()}
                          onClick={dismissSearchKeyboard}
                        >
                          ซ่อนแป้น
                        </button>
                      ) : null}
                    </div>
                    <input
                      ref={searchInputRef}
                      id="pos-stock-batch-search"
                      value={query}
                      onChange={(event) => { setQuery(event.target.value); setError(null); }}
                      onFocus={() => setSearchActive(true)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          dismissSearchKeyboard();
                        }
                      }}
                      placeholder="ชื่อสินค้า SKU หรือบาร์โค้ด"
                      autoComplete="off"
                      enterKeyHint="search"
                    />
                  </div>
                  <div className="pos-stock-batch__catalog">
                    {candidates.map((item) => {
                      const onHand = item.quantity_on_hand == null ? null : Number(item.quantity_on_hand);
                      const added = addedIds.has(item.stock_item_id);
                      return (
                        <button
                          type="button"
                          key={item.stock_item_id}
                          className={added ? "is-added" : ""}
                          onClick={() => void addItem(item)}
                        >
                          <span>
                            <strong>{item.canonical_name}</strong>
                            <small>{item.sku_code ? `SKU ${item.sku_code}` : item.item_code}</small>
                          </span>
                          <span className="pos-stock-batch__catalog-balance">
                            <small>คงเหลือ</small>
                            <strong>{quantityLabel(onHand)}</strong>
                            {added ? <em>อยู่ในชุดแล้ว</em> : null}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : (
                <section className="pos-stock-batch__review-intro">
                  <span>ตรวจสอบก่อนบันทึก</span>
                  <strong>{draft.length.toLocaleString("th-TH")} รายการ</strong>
                  <p>สินค้าเริ่มที่ 1 และแสดงยอดคงเหลือจริง แม้ยอดติดลบ และยังปรับ − / + หรือแตะตัวเลขเพื่อแก้ได้ก่อนบันทึก</p>
                </section>
              )}

              <section className="pos-stock-batch__draft" aria-label="รายการเบิกใช้">
                <div className="pos-stock-batch__draft-heading">
                  <div><span>รายการที่จะเบิก</span><strong>{draft.length.toLocaleString("th-TH")}</strong></div>
                  {invalidCount ? <em>{invalidCount.toLocaleString("th-TH")} รายการต้องตรวจ</em> : <em className="is-ready">พร้อมตรวจ</em>}
                </div>

                {!draft.length ? (
                  <div className="pos-stock-batch__empty">
                    <strong>ยังไม่มีสินค้าในชุด</strong>
                    <span>แตะสินค้าจากด้านซ้าย ระบบจะเริ่มจำนวนที่ 1 แล้วปรับได้ทันที</span>
                  </div>
                ) : (
                  <div className="pos-stock-batch__lines">
                    {draft.map((line, index) => {
                      const itemError = errors.get(line.item.stock_item_id);
                      const quantity = Number(line.quantity);
                      const onHand = line.item.quantity_on_hand == null ? null : Number(line.item.quantity_on_hand);
                      const quantityReady = Number.isFinite(quantity) && quantity > 0;
                      const canDecrease = quantityReady && quantity - 1 > 0;
                      const canIncrease = quantityReady && Number.isFinite(quantity + 1);
                      const inputId = `pos-stock-batch-qty-${line.item.stock_item_id}`;
                      return (
                        <article
                          key={line.item.stock_item_id}
                          className={duplicateItemId === line.item.stock_item_id ? "is-duplicate" : ""}
                        >
                          <div className="pos-stock-batch__line-no">{index + 1}</div>
                          <div className="pos-stock-batch__line-name">
                            <strong>{line.item.canonical_name}</strong>
                            <span>คงเหลือ {quantityLabel(line.item.quantity_on_hand)} {line.unitName}</span>
                          </div>
                          <div className={`pos-stock-batch__quantity${itemError ? " has-error" : ""}`}>
                            <label htmlFor={inputId}>จำนวนเบิก</label>
                            <div className="pos-stock-batch__quantity-controls">
                              <button
                                type="button"
                                onClick={() => stepQuantity(line.item.stock_item_id, -1)}
                                disabled={reviewing || busy || !canDecrease}
                                aria-label={`ลดจำนวน ${line.item.canonical_name}`}
                              >
                                −
                              </button>
                              <input
                                id={inputId}
                                type="number"
                                min="0"
                                step="any"
                                inputMode="decimal"
                                value={line.quantity}
                                onFocus={(event) => event.currentTarget.select()}
                                onChange={(event) => changeQuantity(line.item.stock_item_id, event.target.value)}
                                placeholder="—"
                                disabled={reviewing || busy}
                                aria-invalid={Boolean(itemError)}
                              />
                              <button
                                type="button"
                                onClick={() => stepQuantity(line.item.stock_item_id, 1)}
                                disabled={reviewing || busy || !canIncrease}
                                aria-label={`เพิ่มจำนวน ${line.item.canonical_name}`}
                              >
                                +
                              </button>
                              <strong>{line.unitName}</strong>
                            </div>
                            {itemError ? <small>{itemError}</small> : null}
                          </div>
                          {!reviewing ? (
                            <button type="button" className="pos-stock-batch__remove" onClick={() => removeItem(line.item.stock_item_id)} aria-label={`ลบ ${line.item.canonical_name}`}>×</button>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                )}

                <label className="pos-stock-batch__note">
                  <span>รายละเอียด{requiresNote ? " *" : " (ถ้ามี)"}</span>
                  <textarea
                    value={note}
                    onChange={(event) => changeNote(event.target.value)}
                    rows={2}
                    maxLength={2000}
                    placeholder="เช่น เบิกใช้ก่อนปิดกะ"
                    disabled={reviewing || busy}
                  />
                </label>

                {error ? <div className="pos-stock-batch__error">{error}</div> : null}
              </section>
            </main>

            <footer className="pos-stock-batch__footer">
              {reviewing ? (
                <>
                  <button type="button" className="secondary" onClick={() => { setReviewing(false); setError(null); }} disabled={busy}>กลับไปแก้</button>
                  <div className="pos-stock-batch__footer-summary"><strong>{draft.length.toLocaleString("th-TH")} รายการ</strong><span>จะบันทึกเป็น 1 Batch</span></div>
                  <button type="button" className="primary" onClick={() => void submit()} disabled={busy || !canReview || !online}>
                    {busy ? "กำลังบันทึก…" : `ยืนยันเบิกใช้ ${draft.length.toLocaleString("th-TH")} รายการ`}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="secondary" onClick={onClose} disabled={busy}>ยกเลิก</button>
                  <div className="pos-stock-batch__footer-summary"><strong>{draft.length.toLocaleString("th-TH")} รายการ</strong><span>{invalidCount ? `ต้องตรวจ ${invalidCount.toLocaleString("th-TH")}` : draft.length ? "จำนวนครบแล้ว" : "เพิ่มสินค้าได้หลายรายการ"}</span></div>
                  <button type="button" className="primary" onClick={startReview} disabled={!canReview}>ตรวจสอบและบันทึก</button>
                </>
              )}
            </footer>
          </>
        )}
      </section>
    </div>
  );
}
